import {api} from './api';

interface ApiResponse<T> {
    code: string;
    success: boolean;
    message: string;
    data: T;
}

export interface User {
    userId?: number;
    username: string;
    password?: string;
    email?: string;
    status?: string;
    roles?: Role[];
    permissions?: Permission[];
    createdTime?: string;
    updatedTime?: string;
    roleIds?: number[];
    isOnline?: boolean;
}

interface Role {
    roleId: number;
    roleCode: string;
    roleName: string;
    description?: string;
}

interface Permission {
    permissionId: number;
    permissionCode: string;
    permissionName: string;
    permissionType: string;
    resourceType?: string;
    accessMode?: number;
    functionCode?: string;
}

export const userManagementService = {
    /**
     * 获取所有用户列表
     */
    getUserList: async (): Promise<User[]> => {
        const response = await api.get<ApiResponse<User[]>>('/api/v1/users/management');
        return response.data.data || [];
    },

    /**
     * 获取用户详情（含角色、权限）
     */
    getUserDetail: async (id: number): Promise<User> => {
        const response = await api.get<ApiResponse<User>>(`/api/v1/users/management/${id}`);
        return response.data.data!;
    },

    /**
     * 创建用户
     */
    createUser: async (user: User): Promise<User> => {
        const response = await api.post<ApiResponse<User>>('/api/v1/users/management', user);
        return response.data.data!;
    },

    /**
     * 更新用户信息
     */
    updateUser: async (id: number, user: Partial<User>): Promise<User> => {
        const response = await api.put<ApiResponse<User>>(`/api/v1/users/management/${id}`, user);
        return response.data.data!;
    },

    /**
     * 启用/禁用用户
     */
    setUserStatus: async (id: number, enabled: boolean): Promise<User> => {
        const response = await api.put<ApiResponse<User>>(`/api/v1/users/management/${id}/status?enabled=${enabled}`, {});
        return response.data.data!;
    },

    /**
     * 为用户分配角色
     */
    assignRoles: async (id: number, roleIds: number[]): Promise<User> => {
        const response = await api.put<ApiResponse<User>>(`/api/v1/users/management/${id}/roles`, roleIds);
        return response.data.data!;
    },

    /**
     * 管理员重置用户密码
     */
    resetPassword: async (id: number, newPassword: string): Promise<void> => {
        const response = await api.put<ApiResponse<null>>(`/api/v1/users/admin/users/${id}/password`, {newPassword});
        if (!response.data.success) {
            throw new Error(response.data.message || '重置密码失败');
        }
    },

    /**
     * 强制用户下线
     */
    forceLogout: async (userId: number): Promise<void> => {
        const response = await api.post<ApiResponse<null>>(`/api/v1/users/management/${userId}/force-logout`);
        if (!response.data.success) {
            throw new Error(response.data.message || '强制下线失败');
        }
    },
};